import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mutations } from '../../api';
import { isImageUploadFile, isSvgFile, isVideoUploadFile } from '../../api/progressiveUploadService';
import { toaster } from './toastNotification';

export const useStatusUpload = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [showCaptionModal, setShowCaptionModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [caption, setCaption] = useState('');
  const createStatusMutation = mutations.useCreateStatus();
  const { t } = useTranslation();

  const validateFile = (file: File): boolean => {
    if (isSvgFile(file)) {
      toaster('error', t('svg_not_allowed'));
      return false;
    }
    if (!isImageUploadFile(file) && !isVideoUploadFile(file)) {
      console.error('Please select a valid image or video file');
      return false;
    }
    return true;
  };

  const validateVideoDuration = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        if (video.duration > 30) {
          console.error('Video must be 30 seconds or less');
          resolve(false);
        } else {
          resolve(true);
        }
      };
      video.onerror = () => {
        console.error('Error loading video');
        resolve(false);
      };
      video.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (!file) return;
    if (!validateFile(file)) return;

    if (file.type.startsWith('video/')) {
      const isValid = await validateVideoDuration(file);
      if (!isValid) return;
    }

    setSelectedFile(file);
    setShowCaptionModal(true);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('status', selectedFile);
      formData.append('type', selectedFile.type.startsWith('image/') ? 'image' : 'video');

      if (caption.trim()) {
        formData.append('caption', caption.trim());
      }

      await createStatusMutation.mutateAsync(formData);

      resetUploadState();
    } catch (error: any) {
      console.error(error?.response?.data?.message || 'Failed to upload status');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadState = () => {
    setSelectedFile(null);
    setCaption('');
    setShowCaptionModal(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileDialog = () => {
    if (!showCaptionModal && !selectedFile) {
      fileInputRef.current?.click();
    }
  };

  return {
    fileInputRef,
    uploading,
    showCaptionModal,
    selectedFile,
    caption,
    setCaption,
    handleFileSelect,
    handleUpload,
    resetUploadState,
    openFileDialog,
    setShowCaptionModal,
  };
};
